import { useCallback, useMemo } from 'react'
import throttle from 'lodash/throttle'

const THROTTLE_MS = 200
const API_URL = 'https://places.googleapis.com/v1'
const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY

const DEFAULT_SUGGESTION_BODY = {
  includedRegionCodes: ['us'],
  includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
}
const DEFAULT_DETAILS_FIELD_MASK = 'addressComponents'

/**
 * Google Places autocomplete backed by the Places API (New) REST endpoints.
 *
 * The legacy `google.maps.places.AutocompleteService` JS SDK class is no
 * longer available to new customers as of 2025-03-01 and emits a deprecation
 * warning. Calling the REST API directly avoids the SDK entirely — the new
 * endpoints also send CORS headers, unlike the legacy `maps/api/place/*`
 * web service.
 */
export default function useGooglePlacesAutocomplete() {
  const fetchSuggestions = useMemo(
    () =>
      throttle(async (input, body) => {
        const response = await fetch(`${API_URL}/places:autocomplete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': API_KEY,
          },
          body: JSON.stringify({ input, ...body }),
        })
        if (!response.ok) return []
        const data = await response.json()
        return (data.suggestions ?? [])
          .filter((s) => s.placePrediction)
          .map(({ placePrediction: p }) => ({
            place_id: p.placeId,
            description: p.text?.text ?? '',
            structured_formatting: {
              main_text: p.structuredFormat?.mainText?.text ?? '',
              secondary_text: p.structuredFormat?.secondaryText?.text ?? '',
            },
          }))
      }, THROTTLE_MS),
    []
  )

  const fetchPlaceDetails = useMemo(
    () =>
      throttle(async (placeId, fieldMask) => {
        const response = await fetch(`${API_URL}/places/${placeId}`, {
          headers: {
            'X-Goog-Api-Key': API_KEY,
            'X-Goog-FieldMask': fieldMask,
          },
        })
        if (!response.ok) return null
        return response.json()
      }, THROTTLE_MS),
    []
  )

  const getAddressSuggestions = useCallback(
    async (search, body = DEFAULT_SUGGESTION_BODY) => {
      if (!search || search.length < 2) return []
      return (await fetchSuggestions(search, body)) ?? []
    },
    [fetchSuggestions]
  )

  const getPlaceDetails = useCallback(
    async (placeId, fieldMask = DEFAULT_DETAILS_FIELD_MASK) => {
      if (!placeId) return null

      const result = await fetchPlaceDetails(placeId, fieldMask)
      if (!result?.addressComponents) return null

      let address1 = '',
        city = '',
        state = '',
        county = '',
        postcode = ''

      for (const component of result.addressComponents) {
        switch (component.types[0]) {
          case 'street_number':
            address1 = component.longText + ' ' + address1
            break

          case 'route':
            address1 += component.shortText
            break

          case 'locality':
            city = component.shortText
            break

          case 'administrative_area_level_1':
            state = component.shortText
            break

          case 'administrative_area_level_2':
            county = component.shortText.replace(' County', '')
            break

          case 'postal_code':
            postcode = component.longText + postcode
            break

          case 'postal_code_suffix':
            postcode = postcode + '-' + component.longText
            break

          default:
            break
        }
      }

      return { address1, city, state, county, postcode }
    },
    [fetchPlaceDetails]
  )

  return { getAddressSuggestions, getPlaceDetails }
}
